import { useState, useEffect, useMemo, useRef } from 'react';
import Popup, { ToolbarItem } from 'devextreme-react/popup';
import Form, { SimpleItem, GroupItem, Label, RequiredRule } from 'devextreme-react/form';
import { useApiConfiguration } from '../../../api/api.configuration';
import { Analitos_ResultsTypes } from '../../../utils/systemTypes';
import { useScreenSize } from '../../../utils/media-query';
import { newToast } from '../../../components';
import { ModalResultTypes } from './ModalResultTypes';
import './ModalAnalyte.scss';

type AnalyteMode = 'create' | 'edit' | 'view';

interface ModalAnalyteProps {
  visible: boolean;
  mode: AnalyteMode;
  analyte?: any; // registro completo del analito (edit/view)
  onClose: () => void;
  onSaved?: (analyte: any) => void; // se dispara tras crear/editar con éxito
  /** Lista completa de analitos: la usa el FormulaBuilder para buscar abreviaturas por ID. */
  analytes?: any[];
}

export const ModalAnalyte = ({ visible, mode, analyte, onClose, onSaved, analytes = [] }: ModalAnalyteProps) => {
  const { newAnalito, setAnalito, getListasAnalitos } = useApiConfiguration();
  const { isSmall, isXSmall, isMedium } = useScreenSize();

  const [lists, setLists] = useState<{ tm: any[]; um: any[]; ra: any[]; cb: any[] }>({ tm: [], um: [], ra: [], cb: [] });
  const [formData, setFormData] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const loadedListsRef = useRef(false);

  const readOnly = mode === 'view';
  const isExisting = mode !== 'create' && !!analyte?.An_Id;

  // Cargar los lookups (una sola vez por apertura)
  useEffect(() => {
    if (!visible || loadedListsRef.current) return;
    loadedListsRef.current = true;
    getListasAnalitos().then((res: any) => {
      setLists({
        tm: res?.TiposMuestras || [],
        um: res?.UnidadesMedida || [],
        ra: res?.RangoAlternos || [],
        cb: res?.ConBarras || [],
      });
    });
  }, [visible, getListasAnalitos]);

  // Inicializar el formulario al abrir
  useEffect(() => {
    if (!visible) {
      loadedListsRef.current = false;
      return;
    }
    if (mode === 'create') {
      setFormData({ An_Estado: true, An_Decimales: 0, An_ConservarCerosDecimales: false });
    } else {
      setFormData({ ...(analyte || {}) });
    }
  }, [visible, mode, analyte]);

  const handleSave = async () => {
    if (!formData.An_Nombre || !formData.An_Abreviatura) {
      newToast('WARNING', 'El nombre y la abreviatura son obligatorios', 3);
      return;
    }
    if (formData.An_Tr_Id == null) {
      newToast('WARNING', 'Debe seleccionar el tipo de resultado', 3);
      return;
    }
    if (formData.An_Tm_Id == null) {
      newToast('WARNING', 'Debe seleccionar el tipo de muestra', 3);
      return;
    }

    setSaving(true);
    try {
      if (mode === 'create') {
        const res = await newAnalito({ Analito: formData });
        if (res === 105) {
          onSaved?.(formData);
          onClose();
        }
      } else {
        const res = await setAnalito({ Analito: formData, IdAnalito: analyte.An_Id });
        if (res === 107) {
          onSaved?.({ ...analyte, ...formData });
          onClose();
        }
      }
    } catch (error) {
      console.error('Error guardando analito:', error);
      newToast('ERROR', 'Error al guardar el analito', 4);
    } finally {
      setSaving(false);
    }
  };

  // Props para reutilizar ModalResultTypes (config avanzada del tipo de resultado)
  const configProps = useMemo(() => {
    if (!isExisting) return null;
    return {
      TipoResultado: formData.An_Tr_Id ?? analyte?.An_Tr_Id,
      IdAnalito: analyte.An_Id,
      Analito: formData.An_Nombre ?? analyte?.An_Nombre ?? '',
      ValRefText: {
        An_ValRefText: analyte?.An_ValRefText ?? '',
        An_ValCritico: analyte?.An_ValCritico ?? '',
        An_ValRefTextPermitidos: analyte?.An_ValRefTextPermitidos ?? '',
      },
      configPlantillas: analyte?.An_Plantilla ?? {},
    };
  }, [isExisting, analyte, formData.An_Tr_Id, formData.An_Nombre]);

  const title = mode === 'create' ? 'NUEVO ANALITO' : mode === 'edit' ? `EDITAR ANALITO — ${analyte?.An_Nombre ?? ''}` : `ANALITO — ${analyte?.An_Nombre ?? ''}`;

  // En móvil/tablet el popup ocupa toda la pantalla: así el formulario tiene
  // el alto real del viewport y el scroll queda dentro del cuerpo, no en la página.
  const isMobile = isXSmall || isSmall;

  return (
    <>
      <Popup
        wrapperAttr={{ class: 'modal-analyte' }}
        width={isMobile ? '100%' : isMedium ? '90%' : 720}
        height={isMobile ? '100%' : 'auto'}
        maxWidth={isMedium ? 900 : undefined}
        maxHeight={isMobile ? undefined : '90%'}
        fullScreen={isMobile}
        visible={visible}
        onHiding={onClose}
        title={title}
        showTitle={true}
        showCloseButton={true}
        dragEnabled={!isMobile}
        hideOnOutsideClick={false}>
        <div className='modal-analyte-body'>
        <Form formData={formData} readOnly={readOnly} labelLocation='top' colCount={1} showValidationSummary={false}>
          <GroupItem colCount={isXSmall ? 1 : 2}>
            <SimpleItem dataField='An_Nombre' editorOptions={{ stylingMode: 'outlined' }}>
              <Label text='Analito' />
              <RequiredRule message='El nombre es requerido' />
            </SimpleItem>
            <SimpleItem dataField='An_Abreviatura' editorOptions={{ stylingMode: 'outlined' }}>
              <Label text='Abreviatura' />
              <RequiredRule message='La abreviatura es requerida' />
            </SimpleItem>

            <SimpleItem
              dataField='An_Tr_Id'
              editorType='dxSelectBox'
              editorOptions={{ dataSource: Analitos_ResultsTypes, valueExpr: 'Value', displayExpr: 'State', searchEnabled: true, stylingMode: 'outlined', placeholder: 'Seleccione...' }}>
              <Label text='Tipo de resultado' />
              <RequiredRule message='El tipo de resultado es requerido' />
            </SimpleItem>
            <SimpleItem
              dataField='An_Um_Id'
              editorType='dxSelectBox'
              editorOptions={{ dataSource: lists.um, valueExpr: 'Um_Id', displayExpr: 'Um_Nombre', searchEnabled: true, showClearButton: true, stylingMode: 'outlined', placeholder: 'Seleccione...' }}>
              <Label text='Unidad de medida' />
            </SimpleItem>

            <SimpleItem
              dataField='An_Tm_Id'
              editorType='dxSelectBox'
              editorOptions={{ dataSource: lists.tm, valueExpr: 'Tm_Id', displayExpr: 'Tm_Nombre', searchEnabled: true, stylingMode: 'outlined', placeholder: 'Seleccione...' }}>
              <Label text='Tipo de muestra' />
              <RequiredRule message='El tipo de muestra es requerido' />
            </SimpleItem>
            <SimpleItem
              dataField='An_Ra_Id'
              editorType='dxSelectBox'
              editorOptions={{ dataSource: lists.ra, valueExpr: 'Ra_Id', displayExpr: 'Ra_Nombre', searchEnabled: true, showClearButton: true, stylingMode: 'outlined', placeholder: 'Seleccione...' }}>
              <Label text='Rango alterno' />
            </SimpleItem>

            <SimpleItem
              dataField='An_Cb_Id'
              editorType='dxSelectBox'
              editorOptions={{ dataSource: lists.cb, valueExpr: 'Cb_Id', displayExpr: 'Cb_Nombre', searchEnabled: true, showClearButton: true, stylingMode: 'outlined', placeholder: 'Seleccione...' }}>
              <Label text='Código de barras' />
            </SimpleItem>
            <SimpleItem dataField='An_Decimales' editorType='dxNumberBox' editorOptions={{ min: 0, max: 10, showSpinButtons: true, stylingMode: 'outlined' }}>
              <Label text='Decimales' />
            </SimpleItem>

            <SimpleItem dataField='An_Homologacion' editorOptions={{ stylingMode: 'outlined' }}>
              <Label text='Homologación' />
            </SimpleItem>
            <SimpleItem dataField='An_ConservarCerosDecimales' editorType='dxCheckBox'>
              <Label text='Conservar ceros decimales' />
            </SimpleItem>

            <SimpleItem dataField='An_Estado' editorType='dxCheckBox'>
              <Label text='Activo' />
            </SimpleItem>
          </GroupItem>
        </Form>
        </div>

        {isExisting && (
          <ToolbarItem
            widget='dxButton'
            location='before'
            toolbar='bottom'
            options={{
              text: isXSmall ? 'Configurar resultado' : 'Configurar tipo de resultado',
              icon: 'preferences',
              stylingMode: 'text',
              type: 'default',
              onClick: () => setShowConfig(true),
            }}
          />
        )}
        <ToolbarItem widget='dxButton' location='after' toolbar='bottom' options={{ text: readOnly ? 'Cerrar' : 'Cancelar', stylingMode: 'outlined', onClick: onClose }} />
        {!readOnly && <ToolbarItem widget='dxButton' location='after' toolbar='bottom' options={{ text: saving ? 'Guardando...' : 'Guardar', type: 'default', stylingMode: 'contained', disabled: saving, onClick: handleSave }} />}
      </Popup>

      {showConfig && configProps && (
        <ModalResultTypes
          key={`cfg-${configProps.IdAnalito}`}
          visible={showConfig}
          setVisible={() => setShowConfig(false)}
          TipoResultado={configProps.TipoResultado}
          IdAnalito={configProps.IdAnalito}
          Analito={configProps.Analito}
          ValRefText={configProps.ValRefText}
          configPlantillas={configProps.configPlantillas}
          Analytes={analytes}
        />
      )}
    </>
  );
};
